/* eslint-disable react/prop-types */
import { Component } from "react";
import ReactFlow, { ReactFlowProvider, useReactFlow } from "reactflow";
import {
  Typography,
  IconButton,
  Box,
  Drawer,
  TextField,
  Button,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { HelpOutline } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PtwSettingsData } from "./workFlow-context";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useTheme, lighten, darken } from "@mui/material/styles";
import { getSingleSelectStyles } from "@/lib/styles";
import useDuplicateWorkflowData from "./useDuplicateWorkflowData";
import "reactflow/dist/style.css";
import Toast from "@/components/toast";
import { useState, useEffect } from "react";
import NewDeleteModal from "@/components/newDeleteModal";
import { useTranslation } from "react-i18next";
import DuplicateWorkflowUserGuide from "@/components/dialogs/DuplicateWorkflowUserGuide";
import StepModalDialog from "./components/StepModalDialog";
import DeleteStepInputDialog from "./components/DeleteStepInputDialog";

const animatedComponents = makeAnimated();

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    const { t } = this.props;
    if (this.state.hasError) {
      return (
        <Typography color="error">
          {t("duplicateWorkflow.errorRendering", {
            message:
              this.state.error?.message || t("duplicateWorkflow.unknownError"),
          })}
        </Typography>
      );
    }
    return this.props.children;
  }
}

// Child component to handle ReactFlow and fitView logic
const FlowContainer = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  drawerOpen,
  isDarkMode,
  t,
}) => {
  const { fitView } = useReactFlow();
  const theme = useTheme();

  // Fit the view whenever nodes, edges, or drawer state changes
  useEffect(() => {
    if (nodes.length > 0) {
      const timeout = setTimeout(() => {
        fitView({ padding: 0.1, duration: 200 });
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [nodes, edges, drawerOpen, fitView]);

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        defaultEdgeOptions={{
          animated: true,
          style: {
            stroke: theme.palette.primary.dark,
            strokeWidth: 2,
          },
        }}
        fitView
        fitViewOptions={{ padding: 0.1 }}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 8,
          border: `1px solid ${
            isDarkMode
              ? lighten(theme.palette.divider, 0.8)
              : darken(theme.palette.divider, 0.5)
          }`,
        }}
        aria-label={t("duplicateWorkflow.flowAria")}
      />
    </>
  );
};

const DuplicateWorkflow = () => {
  const { t } = useTranslation();
  const {
    workflowData,
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    isModalOpen,
    closeModal,
    nodeData,
    setNodeData,
    userGroupIds,
    modalButton,
    addChildNode,
    updateNodeData,
    changeHandlerName,
    handleSubmit,
    changeTarget,
    handleMultiChange,
    handleInputTypeChange,
    addNewStepInput,
    removeStepInput,
    addOption,
    removeOption,
    toast,
    handleToastClose,
    userGroupsList,
    inputTypeList,
    moveStepInputDown,
    moveStepInputUp,
    confirmDeleteNode,
    deleteDialogOpen,
    setDeleteDialogOpen,
    drawerOpen,
    setDrawerOpen,
  } = useDuplicateWorkflowData();
  const { goBackToList } = PtwSettingsData();

  const theme = useTheme();
  const singleSelectStyles = getSingleSelectStyles(theme);
  const [isFormValid, setIsFormValid] = useState(false);
  const [optionInputs, setOptionInputs] = useState({});
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    stepIndex: null,
    stepName: "",
  });
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const isDarkMode = theme.palette.mode === "dark";

  const drawerWidth = drawerOpen ? 360 : 60;
  // Dynamic toolbar height with extra spacing for app bar shadows/styling
  const toolbarHeight = (theme.mixins.toolbar.minHeight || 64) + 16;
  const isValid = workflowData?.name?.length > 2 && workflowData?.target;

  // Validate all required fields, including step_inputs and options
  useEffect(() => {
    const validateForm = () => {
      const staticFieldsValid =
        userGroupIds.length > 0 &&
        Boolean(nodeData.action_name) &&
        Boolean(nodeData.status);

      if (!staticFieldsValid) return false;

      if (
        !Array.isArray(nodeData.step_inputs) ||
        nodeData.step_inputs.length === 0
      ) {
        return true;
      }

      const stepInputsValid = nodeData.step_inputs.every((step_input) => {
        if (!step_input.input_type_id || !step_input.name) {
          return false;
        }

        const inputType = inputTypeList.find(
          (option) => option.value === step_input.input_type_id
        );
        if (
          inputType &&
          ["selection", "multi_selection"].includes(inputType.input_key) &&
          (!step_input.options || step_input.options.length === 0)
        ) {
          return false;
        }

        return true;
      });

      return staticFieldsValid && stepInputsValid;
    };

    setIsFormValid(validateForm());
  }, [
    userGroupIds.length,
    nodeData.action_name,
    nodeData.status,
    nodeData.step_inputs,
    inputTypeList,
  ]);

  const handleOptionInputChange = (index, value) => {
    setOptionInputs((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  const handleAddOption = (index) => {
    const optionInput = optionInputs[index] || "";
    if (!optionInput.trim()) return;

    addOption(index, optionInput);
    setOptionInputs((prev) => ({
      ...prev,
      [index]: "",
    }));
  };

  const handleDeleteStepInput = (index) => {
    const stepName =
      nodeData.step_inputs?.[index]?.name ||
      `${t("duplicateWorkflow.stepInput")} ${index + 1}`;
    setDeleteConfirmation({
      open: true,
      stepIndex: index,
      stepName: stepName,
    });
  };

  const confirmDeleteStepInput = () => {
    const index = deleteConfirmation.stepIndex;
    removeStepInput(index);
    setDeleteConfirmation({ open: false, stepIndex: null, stepName: "" });
  };

  const cancelDeleteStepInput = () => {
    setDeleteConfirmation({ open: false, stepIndex: null, stepName: "" });
  };

  if (!workflowData) {
    return <Typography>{t("duplicateWorkflow.loadingWorkflow")}</Typography>;
  }

  const targetOptions = [
    { value: "WORK_PERMIT", label: t("duplicateWorkflow.targetWorkPermit") },
    { value: "OBSERVATION", label: t("duplicateWorkflow.targetObservation") },
  ];

  return (
    <ErrorBoundary t={t}>
      <Box
        sx={{
          display: "flex",
          height: `calc(100vh - ${toolbarHeight}px - 32px)`,
          width: "100%",
          position: "relative",
          overflow: "hidden",
          mt: `${toolbarHeight}px`,
          p: 0,
          m: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: "100%",
            height: "100%",
            position: "relative",
            p: 0,
            m: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 32,
              left: 16,
              zIndex: 1000,
              display: "flex",
              gap: 1,
            }}
          >
            <IconButton
              sx={{
                bgcolor: theme.palette.background.paper,
                boxShadow: theme.shadows[2],
                "&:hover": {
                  bgcolor: theme.palette.background.paper,
                  boxShadow: theme.shadows[4],
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
              onClick={goBackToList}
              aria-label={t("duplicateWorkflow.backButtonAria")}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              sx={{
                bgcolor: theme.palette.info.main,
                color: theme.palette.info.contrastText,
                boxShadow: theme.shadows[2],
                "&:hover": {
                  bgcolor: theme.palette.info.dark,
                  boxShadow: theme.shadows[4],
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
              onClick={() => setHelpModalOpen(true)}
              aria-label={t("duplicateWorkflow.helpButtonAria")}
            >
              <HelpOutline />
            </IconButton>
          </Box>
          <Typography>{t("duplicateWorkflow.saveAsWorkflow")}</Typography>

          <ReactFlowProvider>
            <FlowContainer
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              drawerOpen={drawerOpen}
              isDarkMode={isDarkMode}
              t={t}
            />
          </ReactFlowProvider>
        </Box>

        <Drawer
          variant="permanent"
          anchor="right"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            zIndex: theme.zIndex.drawer, // Standard drawer z-index
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              transition: "width 0.3s ease",
              bgcolor: theme.palette.background.paper,
              borderLeft: `1px solid ${
                isDarkMode
                  ? lighten(theme.palette.divider, 0.2)
                  : darken(theme.palette.divider, 0.2)
              }`,
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              top: `${toolbarHeight}px`,
              height: `calc(100vh - ${toolbarHeight}px)`,
            },
          }}
          aria-label={t("duplicateWorkflow.drawerAria")}
        >
          <Box
            sx={{
              width: "100%",
              height: 60,
              display: "flex",
              alignItems: "center",
              justifyContent: drawerOpen ? "flex-end" : "center",
              px: 1,
            }}
          >
            <IconButton
              onClick={() => {
                setDrawerOpen(!drawerOpen);
              }}
              sx={{
                color: theme.palette.text.primary,
                "&:hover": {
                  bgcolor: theme.palette.action.hover,
                },
              }}
              aria-label={t(
                drawerOpen
                  ? "duplicateWorkflow.closeDrawerAria"
                  : "duplicateWorkflow.openDrawerAria"
              )}
            >
              {drawerOpen ? (
                <ChevronRightIcon fontSize="medium" />
              ) : (
                <ChevronLeftIcon fontSize="medium" />
              )}
            </IconButton>
          </Box>

          {drawerOpen && (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: "100%",
                p: 2,
                overflowY: "auto",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
              aria-label={t("duplicateWorkflow.formAria")}
            >
              {/* Header Section */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    color: "primary.main",
                    mb: 1,
                  }}
                >
                  {t("duplicateWorkflow.title")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t("duplicateWorkflow.drawerDescription")}
                </Typography>
              </Box>

              {/* Workflow Configuration Section */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor:
                      theme.palette.mode === "dark" ? "grey.900" : "grey.50",
                    borderRadius: 2,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="h6"
                    color="primary"
                    gutterBottom
                    sx={{
                      fontWeight: "bold",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {t("duplicateWorkflow.workflowConfigTitle")}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: "medium", mb: 1 }}
                  >
                    {t("duplicateWorkflow.workflowNameLabel")} *
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {t("duplicateWorkflow.workflowNameDescription")}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={workflowData.name || ""}
                    onChange={changeHandlerName}
                    placeholder={t("duplicateWorkflow.workflowNamePlaceholder")}
                    error={!workflowData.name}
                    helperText={
                      !workflowData.name
                        ? t("duplicateWorkflow.workflowNameRequired")
                        : ""
                    }
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        minHeight: 48,
                        background: theme.palette.background.paper,
                      },
                    }}
                    aria-label={t("duplicateWorkflow.workflowNameAria")}
                  />

                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ fontWeight: "medium", mb: 1 }}
                  >
                    {t("duplicateWorkflow.targetLabel")} *
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {t("duplicateWorkflow.targetDescription")}
                  </Typography>
                  <Select
                    components={animatedComponents}
                    options={targetOptions}
                    value={
                      targetOptions.find(
                        (opt) => opt.value === workflowData.target
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      changeTarget(selectedOption?.value || "")
                    }
                    placeholder={t("duplicateWorkflow.targetPlaceholder")}
                    styles={{
                      ...singleSelectStyles,
                      control: (base) => ({
                        ...base,
                        minHeight: 48,
                        borderRadius: 4,
                        background: theme.palette.background.paper,
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    aria-label={t("duplicateWorkflow.targetAria")}
                  />
                </Box>

                {/* Status Indicator */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor:
                      theme.palette.mode === "dark" ? "grey.900" : "grey.50",
                    borderRadius: 2,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("duplicateWorkflow.statusTitle")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: isValid ? "success.main" : "warning.main",
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {isValid
                        ? t("duplicateWorkflow.readyToSave")
                        : t("duplicateWorkflow.fillRequired")}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  mt: "auto",
                  pt: 3,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <Box display="flex" flexDirection="column" gap={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isValid}
                    size="large"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      fontWeight: "bold",
                      textTransform: "none",
                      boxShadow: 2,
                      "&:hover": {
                        boxShadow: 4,
                        transform: "translateY(-1px)",
                      },
                    }}
                    aria-label={t("duplicateWorkflow.saveWorkflowButtonAria")}
                  >
                    {t("duplicateWorkflow.saveWorkflowButton")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={goBackToList}
                    size="large"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      fontWeight: "medium",
                      textTransform: "none",
                    }}
                    aria-label={t("duplicateWorkflow.cancelButtonAria")}
                  >
                    {t("duplicateWorkflow.cancelButton")}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Drawer>
      </Box>

      <StepModalDialog
        open={isModalOpen}
        onClose={closeModal}
        modalButton={modalButton}
        nodeData={nodeData}
        setNodeData={setNodeData}
        userGroupIds={userGroupIds}
        userGroupsList={userGroupsList}
        inputTypeList={inputTypeList}
        handleMultiChange={handleMultiChange}
        isFormValid={isFormValid}
        onSave={modalButton === "add" ? addChildNode : updateNodeData}
        onAddStep={addChildNode}
        handleInputTypeChange={handleInputTypeChange}
        addNewStepInput={addNewStepInput}
        handleDeleteStepInput={handleDeleteStepInput}
        moveStepInputUp={moveStepInputUp}
        moveStepInputDown={moveStepInputDown}
        optionInputs={optionInputs}
        handleOptionInputChange={handleOptionInputChange}
        handleAddOption={handleAddOption}
        removeOption={removeOption}
        translationPrefix="duplicateWorkflow"
      />

      <NewDeleteModal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDeleteNode}
        itemType={t("duplicateWorkflow.deleteItemType")}
      />

      <DeleteStepInputDialog
        open={deleteConfirmation.open}
        onClose={cancelDeleteStepInput}
        onConfirm={confirmDeleteStepInput}
        stepName={deleteConfirmation.stepName}
        translationPrefix="duplicateWorkflow"
      />

      {/* User Guide Help Modal */}
      <DuplicateWorkflowUserGuide
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />

      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </ErrorBoundary>
  );
};

export default DuplicateWorkflow;
